import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ArrowDownTraySvg from '../other/svg/ArrowDownTraySvg'
import MoreHorizontalSvg from '../other/svg/MoreHorizontalSvg'
import OpenWindowSvg from '../other/svg/OpenWindowSvg'
import PencilSvg from '../other/svg/PencilSvg'
import TrashCanSvg from '../other/svg/TrashCanSvg'

interface DocumentListRowProps {
  label: string
  date: string
  isMenuOpen: boolean
  onToggleMenu: () => void
  onCloseMenu: () => void
  onOpen: () => void
  onDownload: () => void
  onEdit: () => void
  onDelete: () => void
  vehicleName?: string
  onOpenVehicle?: () => void
}

interface DocumentMenuItemProps {
  icon: JSX.Element
  label: string
  onClick: () => void
}

const menuShadow =
  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const DocumentMenuItem = ({ icon, label, onClick }: DocumentMenuItemProps): JSX.Element => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-stretch px-2 py-1 bg-backgrounds-bg-field-component rounded-sm inline-flex justify-start items-center gap-2 hover:bg-backgrounds-bg-component-hover">
      <div className="w-4 h-4 relative text-icon-secondary flex justify-center items-center">{icon}</div>
      <div className="flex-1 justify-start text-left text-foregrounds-fg-base text-xs font-medium font-Montserrat leading-5">
        {label}
      </div>
    </button>
  )
}

const DocumentListRow = ({
  label,
  date,
  isMenuOpen,
  onToggleMenu,
  onCloseMenu,
  onOpen,
  onDownload,
  onEdit,
  onDelete,
  vehicleName,
  onOpenVehicle
}: DocumentListRowProps): JSX.Element => {
  const { t } = useTranslation()
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) {
        onCloseMenu()
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClick)
    }
  }, [isMenuOpen, onCloseMenu])

  return (
    <div
      className={`self-stretch h-11 px-4 inline-flex justify-center items-center gap-3 ${
        isMenuOpen
          ? 'bg-backgrounds-bg-highlight'
          : 'bg-backgrounds-bg-field-component hover:bg-backgrounds-bg-highlight'
      }`}>
      <div className="flex-1 self-stretch flex justify-start items-center gap-3">
        <div className="flex-1 self-stretch flex justify-start items-center overflow-hidden">
          <div
            className={`justify-start text-xs font-semibold font-Montserrat leading-5 truncate ${
              typeof vehicleName === 'string'
                ? 'text-foregrounds-fg-subtle'
                : 'text-center text-tag-neutral-tag-neutral-text'
            }`}>
            {label}
          </div>
        </div>
        {typeof vehicleName === 'string' && (
          <div className="flex-1 self-stretch flex justify-start items-center overflow-hidden">
            {typeof onOpenVehicle === 'function' ? (
              <button
                type="button"
                onClick={onOpenVehicle}
                className="justify-start text-foregrounds-fg-interactive text-xs font-semibold font-Montserrat underline leading-5 truncate">
                {vehicleName}
              </button>
            ) : (
              <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5 truncate">
                {vehicleName}
              </div>
            )}
          </div>
        )}
        <div
          className={`self-stretch flex justify-start items-center overflow-hidden ${
            typeof vehicleName === 'string' ? 'w-20' : ''
          }`}>
          <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-Montserrat leading-5 whitespace-nowrap">
            {date}
          </div>
        </div>
        <div className="self-stretch px-px flex justify-end items-center overflow-visible">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={onToggleMenu}
              className={`p-1 rounded-md flex justify-center items-center overflow-hidden ${
                isMenuOpen ? 'bg-zinc-900/10' : 'bg-white/0 hover:bg-zinc-900/10'
              }`}>
              <MoreHorizontalSvg className="w-4 h-4 text-icon-secondary" />
            </button>
            {isMenuOpen && (
              <div
                className="min-w-28 p-1 absolute right-0 top-full z-30 bg-backgrounds-bg-field-component rounded-lg flex flex-col justify-start items-start overflow-hidden"
                style={{ boxShadow: menuShadow }}>
                <div className="self-stretch flex flex-col justify-start items-start">
                  <DocumentMenuItem
                    icon={<OpenWindowSvg className="w-4 h-4" />}
                    label={t('my_vehicles.document_open')}
                    onClick={() => {
                      onCloseMenu()
                      onOpen()
                    }}
                  />
                  <DocumentMenuItem
                    icon={<ArrowDownTraySvg className="w-4 h-4" />}
                    label={t('my_vehicles.document_download')}
                    onClick={() => {
                      onCloseMenu()
                      onDownload()
                    }}
                  />
                  <div className="self-stretch h-2 relative -mx-1">
                    <div className="absolute left-0 right-0 top-[3px] h-px bg-borders-border-menu-top" />
                    <div className="absolute left-0 right-0 top-[4px] h-px bg-borders-border-menu-bot" />
                  </div>
                  <DocumentMenuItem
                    icon={<PencilSvg className="w-4 h-4" />}
                    label={t('my_vehicles.edit')}
                    onClick={() => {
                      onCloseMenu()
                      onEdit()
                    }}
                  />
                  <DocumentMenuItem
                    icon={<TrashCanSvg className="w-4 h-4" />}
                    label={t('my_vehicles.delete')}
                    onClick={() => {
                      onCloseMenu()
                      onDelete()
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DocumentListRow
